import { useMemo } from 'react'

import type { ChartTypeV2 as ChartType } from 'features/charts-v2/types/chart-types'
import { useDetailData } from 'features/trend-detection/features/issue-detail/hooks/use-detail-data'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  Chart,
  IssueCardData,
  IssueResponse,
  PostText,
  UpdateDetailResponse,
} from 'features/trend-detection/types'
import type { IssueBreakdownSelectionValue } from 'features/trend-detection/utils/dimension-mappings'

/** Patch the cached update detail's embedded source issue, keeping the
 *  update-level `is_tracked` in step when tracking changes. */
export function patchUpdateDetailSourceIssue(
  prev: UpdateDetailResponse | undefined,
  patch: Partial<IssueResponse>,
): UpdateDetailResponse | undefined {
  if (!prev) return prev
  return {
    ...prev,
    update: {
      ...prev.update,
      ...(patch.is_tracked !== undefined && { is_tracked: patch.is_tracked }),
      source_issue: { ...prev.update.source_issue, ...patch },
    },
  }
}

export interface UseUpdateDetailResult {
  updateItem: UpdateDetailResponse | null
  /** Card data derived from the source issue (for charts/verbatims rendering). */
  sourceIssueCard: IssueCardData | null
  updateLoading: boolean
  updateError: boolean

  timeseriesChart: Chart | undefined
  projectTotalChart: Chart | undefined
  timeseriesLoading: boolean

  breakdownChart: Chart | null | undefined
  breakdownLoading: boolean
  selectedBreakdownDimension: IssueBreakdownSelectionValue
  breakdownChartType: ChartType
  onBreakdownDimensionChange: (dim: IssueBreakdownSelectionValue) => void
  onBreakdownChartTypeChange: (type: ChartType) => void
  refetchBreakdowns: (dim?: IssueBreakdownSelectionValue) => void

  postTexts: PostText[]
  verbatimsLoading: boolean
}

export function useUpdateDetail(
  service: TrendDetectionService,
  projectId: string | null,
  updateUuid: string | null,
): UseUpdateDetailResult {
  const config = useMemo(
    () => ({
      queryKey: 'update-detail',
      fetchFn: (pid: string, uuid: string) =>
        service.fetchUpdateDetail(pid, uuid),
      getIssueUuid: (item: UpdateDetailResponse) =>
        item.update?.source_issue_uuid ?? null,
      // Read the update's own run_uuid (not the item-level run_uuid, which
      // describes the source issue's originating run) so the chart matches
      // the mini chart on the tracked feed card.
      getRunUuid: (item: UpdateDetailResponse) =>
        item.update?.run_uuid ?? item.run_uuid ?? null,
      getIssue: (item: UpdateDetailResponse) =>
        item.update?.source_issue ?? null,
    }),
    [service],
  )

  const detail = useDetailData<UpdateDetailResponse>(
    service,
    projectId,
    updateUuid,
    config,
  )

  return {
    updateItem: detail.item,
    sourceIssueCard: detail.cardData,
    updateLoading: detail.loading,
    updateError: detail.error,
    timeseriesChart: detail.timeseriesChart,
    projectTotalChart: detail.projectTotalChart,
    timeseriesLoading: detail.timeseriesLoading,
    breakdownChart: detail.breakdownChart,
    breakdownLoading: detail.breakdownLoading,
    selectedBreakdownDimension: detail.selectedBreakdownDimension,
    breakdownChartType: detail.breakdownChartType,
    onBreakdownDimensionChange: detail.onBreakdownDimensionChange,
    onBreakdownChartTypeChange: detail.onBreakdownChartTypeChange,
    refetchBreakdowns: detail.refetchBreakdowns,
    postTexts: detail.postTexts,
    verbatimsLoading: detail.verbatimsLoading,
  }
}
